import Icon from '@/components/ui/AppIcon';

export interface HourBlockItem {
  id: string;
  name: string;
  quantity: number;
  prepTime: number;
  completed: boolean;
}

interface HourBlockProps {
  hour: string;
  items: HourBlockItem[];
  onToggleComplete: (itemId: string) => void;
}

export default function HourBlock({ hour, items, onToggleComplete }: HourBlockProps) {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const completedItems = items.filter((item) => item.completed).length;
  const progress = items.length > 0 ? (completedItems / items.length) * 100 : 0;

  return (
    <div className="min-w-[320px] snap-start shrink-0">
      <div className="glass-card rounded-2xl p-6 h-full">
        {/* Hour Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-black text-foreground font-heading">{hour}</h3>
            <p className="text-sm text-muted-foreground">{totalItems} items total</p>
          </div>
          <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center">
            <div className="text-center">
              <p className="text-xs text-muted-foreground font-bold">Done</p>
              <p className="text-lg font-black text-foreground">
                {completedItems}/{items.length}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-success transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Items List */}
        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="CheckCircleIcon" size={40} className="text-success mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No items scheduled</p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  item.completed
                    ? 'border-success bg-success/5' :'border-border bg-card hover:border-primary'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  {/* Checkbox */}
                  <button
                    onClick={() => onToggleComplete(item.id)}
                    className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all ${
                      item.completed
                        ? 'bg-success border-success' :'border-muted hover:border-primary'
                    }`}
                    aria-label={`Mark ${item.name} as ${item.completed ? 'incomplete' : 'complete'}`}
                  >
                    {item.completed && (
                      <Icon name="CheckIcon" size={16} className="text-white" />
                    )}
                  </button>

                  {/* Item Info */}
                  <div className="flex-1 min-w-0">
                    <h4
                      className={`font-bold mb-1 ${
                        item.completed
                          ? 'text-muted-foreground line-through'
                          : 'text-foreground'
                      }`}
                    >
                      {item.name}
                    </h4>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="HashtagIcon" size={12} />
                        Qty: {item.quantity}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="ClockIcon" size={12} />
                        {item.prepTime} min
                      </span>
                    </div>
                  </div>

                  {/* Quantity Badge */}
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-black ${
                      item.completed
                        ? 'bg-success/10 text-success' :'bg-primary/10 text-primary'
                    }`}
                  >
                    ×{item.quantity}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}